import {
  Grid,
  Card,
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Rating,
  Chip,
  Stack,
  Skeleton,
  Link,
  Checkbox,
  FormControlLabel,
  Alert,
  Divider,
  Switch,
  LinearProgress,
} from '@mui/material';
import moment from 'moment-timezone';
import useAuth from 'src/hooks/useAuth';
import { foodRun, admin, donations, smsBilling } from 'src/backend';
import { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import _get from 'lodash/get';
import { Link as RouterLink } from 'react-router-dom';

import GoogleDirectionsMap from '../GoogleMaps/GoogleDirectionsMap';
import CompleteFoodRunModal, {
  formatRescueFoodWeights,
} from './CompleteFoodRunModal';
import ChangeRecipientModal from './ChangeRecipientModal';
import useFoodRunStatus from 'src/hooks/useFoodRunStatus';
import { BUCKET_NAME, S3_BASE_URL } from 'src/config';
import { parseFoodWeights } from 'src/utils/foodWeights';
import { useEffect } from 'react';

import * as form from 'src/components/form';
import * as Yup from 'yup';
import useNotifyableDrivers from 'src/hooks/useNotifyableDrivers';
import { DISTANCE_UNITS, getDefaultUnit } from '../MeasureField';
import { Distance, formatDisplayDate } from 'careit';
import { useCheckAction } from 'src/utils/permissions';
import { useGetVehicleLabels } from 'src/constants';
import DonationCancelConfirmationModal from 'src/components/DonationCancelConfirmationModal';
import DonationEventLogs from 'src/components/dashboard/details/DonationEventLogs';
import ConfirmModal from '../ConfirmModal';
import {
  CardEmptyState,
  DonationImageGrid,
  DonationNotes,
  InfoContainer,
  TitleInfo,
} from '../dashboard/details/Donation';
import MoreButton from '../MoreButton';
import {
  CancelRounded,
  CreateRounded,
  MarkunreadRounded,
  MoreVert,
  Delete,
  CopyAll,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import useDonationStatus from 'src/hooks/useDonationStatus';
import useRecurringType from 'src/hooks/useRecurringType';
import DriverSelectorModal from '../dashboard/details/DriverSelectorModal';
import DonationCargoDetails from '../DonationCargoDetails';
import DialogCreateDonation from '../DialogCreateDonation';
import {
  canEditFoodRun,
  hasFoodMoverCompletionAuthority,
  isFoodRunSelfUnassign,
  shouldUseWeightsOnlyMode,
} from './foodRunPermissions';

export function groupByUnit(data) {
  const result = [];

  for (const key in data) {
    if (key.endsWith('_unit')) {
      if (key.endsWith('_temp_unit')) continue; // Skip keys that end with '_temp_unit'

      const foodType = key.slice(0, -5); // Remove '_unit' from the end of the key to get the food type
      const estimatedWeight = data[foodType];
      const estimatedWeightUnit = data[key];

      if (estimatedWeight !== null && estimatedWeight !== undefined) {
        result.push({
          food_type: foodType,
          estimated_weight: estimatedWeight,
          estimated_weight_unit: estimatedWeightUnit,
        });
      }
    }
  }

  return result;
}

const FoodRunInfo = ({ data = {}, getFoodRunData, loadingFoodRun }) => {
  const { t } = useTranslation('translation');
  const { getStatus } = useDonationStatus();
  const { getType } = useRecurringType();
  const history = useHistory();
  const [showCreateDonationDialog, setShowCreateDonationDialog] =
    useState(false);
  const [patternData, setPatternData] = useState(null);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showEditConfirmModal, setShowEditConfirmModal] = useState(false);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  const [
    showCancelSingleInstanceConfirmModal,
    setShowCancelSingleInstanceConfirmModal,
  ] = useState(false);
  const [
    showCancelRecurringInstancesConfirmModal,
    setShowCancelRecurringInstancesConfirmModal,
  ] = useState(false);
  const [showDeleteRunModal, setShowDeleteRunModal] = useState(false);
  const [updateCompletionDataLoading, setUpdateCompletionDataLoading] =
    useState(false);
  const [showDriverReqModal, setShowDriverReqModal] = useState(false);
  const [driverAction, setDriverAction] = useState(null);
  const [showChangeRecipientModal, setShowChangeRecipientModal] =
    useState(false);
  const { user } = useAuth();
  const viewOrg =
    user?.account_type === 'ADMIN'
      ? PATH_DASHBOARD.admin.viewOrg
      : PATH_DASHBOARD.viewOrg;
  const { enqueueSnackbar } = useSnackbar();
  const vehicleLabelsWithUnit = useGetVehicleLabels();

  const handleCompleteSuccess = () => {
    setShowCompleteModal(false);
    enqueueSnackbar(t('donations.run-completed-successfully'), {
      variant: 'success',
    });
    getFoodRunData();
  };
  const [isFoodRunEditable, setIsFoodRunEditable] = useState(false);
  const {
    statusLoading,
    unassignLoading,
    cancelLoadingInstance,
    cancelLoadingBulk,
    handleUnAssignRunner,
    handleBeginDelivery,
    handleBeginPickup,
    handleClaim,
    handleComplete,
    handleReAssignRescue,
  } = useFoodRunStatus({
    data,
    getFoodRunData,
    handleCompleteSuccess,
    onCancel: () => setShowCancelConfirmModal(false),
  });
  const [showDriversSelector, setShowDriversSelector] = useState(false);
  const canRequestForDrivers = useCheckAction('request-drivers');

  const labelColor = '#637381';
  const donorContact = JSON.parse(data.donor_contact || '{}');
  const donorLocation = JSON.parse(data.donor_location || '{}');
  const receiverContact = JSON.parse(data.receiver_contact || '{}');
  const receiverLocation = JSON.parse(data.receiver_location || '{}');
  const isCompleted = data.food_run_status === 'COMPLETED';

  const parsedFoodWeightObject = JSON.parse(data.estimated_weights || '[]');

  const drivers = data.rescue_drivers || [];
  const canCompleteFoodRun = hasFoodMoverCompletionAuthority({
    user,
    foodRun: data,
    drivers,
  });
  // A driver leaving the run removes only themselves; mover admins and staff
  // release it from every driver.
  const unassignRunLabel = isFoodRunSelfUnassign({
    user,
    foodRun: data,
    drivers,
  })
    ? t('donations.remove-me-from-this-run')
    : t('donations.unassign-this-run');

  const displayedVehicleName = (() => {
    const driverVehicleNames = drivers
      .map((d) => d.vehicle_name)
      .filter(Boolean);

    if (driverVehicleNames.length > 0) {
      const uniqueNames = Array.from(new Set(driverVehicleNames));
      return uniqueNames.join(', ');
    }

    return null;
  })();

  const totalFoodWeight = parsedFoodWeightObject?.reduce((total, food) => {
    return total + food.estimated_weight;
  }, 0);
  const images = JSON.parse(data.images || '[]');
  const rescuedFoodWeights = formatRescueFoodWeights(data);
  const rescuedFoodWeightsArray = groupByUnit(rescuedFoodWeights);
  // if the user has set a custom lat/lng, then override
  const foodMoverLocation =
    (user?.start_lat && user?.start_lng) || (user?.user_lat && user?.user_lng)
      ? {
          lat: user?.start_lat || user?.user_lat,
          lng: user?.start_lng || user?.user_lng,
        }
      : JSON.parse(data.food_mover_location || '{}');

  const handleUpdateCompletionData = (updates) => {
    setUpdateCompletionDataLoading(true);
    foodRun
      .updateCompletionData({ ...updates, rescueId: data.rescue_id })
      .then(() => {
        setShowCompleteModal(false);
        enqueueSnackbar(t('donations.run-updated-successfully'), {
          variant: 'success',
        });
        getFoodRunData();
      })
      .catch((err) => {
        enqueueSnackbar(err?.message || t('form.errors.generic'), {
          variant: 'error',
        });
        console.error(err);
      })
      .finally(() => setUpdateCompletionDataLoading(false));
  };

  const hardDeleteDonationByAdmin = async () => {
    admin
      .deleteDonation(data.id)
      .then((data) => {
        if (data.status !== 200) {
          throw new Error('Something went wrong');
        }
        setShowDeleteRunModal(false);
        enqueueSnackbar('Deleted the run successfully!', {
          variant: 'success',
        });
        history.goBack();
      })
      .catch(() =>
        enqueueSnackbar(t('form.errors.generic'), {
          variant: 'error',
        })
      );
  };

  const ActionButton = ({ status }) => {
    const UpdateCompletionData = () => {
      return (
        <Box>
          <Button
            fullWidth
            color="primary"
            variant="contained"
            sx={{
              borderRadius: '50px',
            }}
            onClick={() => setShowCompleteModal(true)}
          >
            {t('donations.update-completion-data')}
          </Button>
        </Box>
      );
    };

    const timezone_id = donorLocation.timezone_id ?? user.timezone_id;

    const isAssignedToCurrentUser = drivers.some((d) => d.user_id === user.id);
    const isStarted = moment(data.start_date)
      .tz(timezone_id)
      .isBefore(moment().tz(timezone_id));
    const isExpired = moment(data.end_date)
      .tz(timezone_id)
      .isBefore(moment().tz(timezone_id));

    const canDriverClaimOpenRun =
      user.is_driver &&
      !isAssignedToCurrentUser &&
      (status === 'OPEN' || status == null);

    // Assigned drivers switched into another org they belong to (e.g. the
    // recipient) can still complete their run. Driver management stays with
    // the mover team.
    const canCompleteFromOtherOrg =
      isStarted &&
      !!data.rescue_id &&
      status !== 'COMPLETED' &&
      isAssignedToCurrentUser &&
      !canCompleteFoodRun;

    const isDonorTeam = user.team_id === data.donor_team;
    const isRecipientTeam =
      user.team_id === (data.receiver_team ?? data.recipient_team_id);
    const canUpdateWeightsOnly =
      isStarted &&
      !!data.rescue_id &&
      status !== 'COMPLETED' &&
      (isDonorTeam || isRecipientTeam) &&
      (!user.is_driver || isAssignedToCurrentUser) &&
      !canCompleteFoodRun;

    // Driver workflow buttons are mover-team only; unassigned mover-team
    // drivers keep the claim path for open runs.
    const canUseActionButtons =
      hasFoodMoverCompletionAuthority({ user, foodRun: data, drivers }) ||
      (canDriverClaimOpenRun && user.team_id === data.food_mover_team_id);

    const hasPendingDriverWorkflow =
      !isExpired && ['ASSIGNED', 'PICKUP_IN_PROGRESS'].includes(status);
    const canAddWeights =
      isStarted &&
      !!data.rescue_id &&
      status !== 'COMPLETED' &&
      canCompleteFoodRun;

    if (user.is_driver && !isAssignedToCurrentUser && !canDriverClaimOpenRun) {
      return null;
    } else if (canCompleteFromOtherOrg) {
      return (
        <LoadingButton
          color="primary"
          variant="contained"
          fullWidth
          sx={{
            borderRadius: '50px',
          }}
          onClick={() => setShowCompleteModal(true)}
          loading={statusLoading}
        >
          {t('donations.complete-run')}
        </LoadingButton>
      );
    } else if (canUpdateWeightsOnly) {
      return (
        <LoadingButton
          color="primary"
          variant="outlined"
          fullWidth
          sx={{
            borderRadius: '50px',
          }}
          onClick={() => setShowCompleteModal(true)}
          loading={statusLoading}
        >
          {t('donations.update-weights')}
        </LoadingButton>
      );
    } else if (
      status === 'WAITING_FOR_REASSIGN' &&
      !user.is_driver &&
      canUseActionButtons
    ) {
      return (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: canAddWeights ? 6 : 12 }}>
            <LoadingButton
              color="primary"
              variant="contained"
              fullWidth
              sx={{
                borderRadius: '50px',
                whiteSpace: 'nowrap',
              }}
              onClick={() => {
                setDriverAction('claim');
                setShowDriversSelector(true);
              }}
              loading={statusLoading}
            >
              {t('donations.re-assign-run')}
            </LoadingButton>
          </Grid>
          {canAddWeights && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <LoadingButton
                color="primary"
                variant="outlined"
                fullWidth
                sx={{
                  borderRadius: '50px',
                }}
                onClick={() => setShowCompleteModal(true)}
                loading={statusLoading}
              >
                {t('donations.add-weights')}
              </LoadingButton>
            </Grid>
          )}
        </Grid>
      );
    } else if (
      isStarted &&
      !!data.rescue_id &&
      status !== 'COMPLETED' &&
      canCompleteFoodRun &&
      !hasPendingDriverWorkflow
    ) {
      return (
        <LoadingButton
          color="primary"
          variant="outlined"
          fullWidth
          sx={{
            borderRadius: '50px',
          }}
          onClick={() => setShowCompleteModal(true)}
          loading={statusLoading}
        >
          {t('donations.add-weights')}
        </LoadingButton>
      );
    } else if (
      isStarted &&
      !!data.rescue_id &&
      status !== 'COMPLETED' &&
      user.team_id === data.food_mover_team_id &&
      drivers.some((d) => d.user_id === user.id) &&
      !hasPendingDriverWorkflow
    ) {
      return (
        <LoadingButton
          color="secondary"
          variant="outlined"
          fullWidth
          sx={{
            borderRadius: '50px',
          }}
          onClick={() => setShowCompleteModal(true)}
          loading={statusLoading}
        >
          {t('donations.add-weights')}
        </LoadingButton>
      );
    } else if (isExpired && !!data.rescue_id && status !== 'COMPLETED') {
      return null;
    } else if (isStarted && status === 'ASSIGNED' && !data.rescue_id) {
      return (
        <LoadingButton
          color="primary"
          variant="contained"
          fullWidth
          sx={{
            borderRadius: '50px',
          }}
          onClick={() => setShowCompleteModal(true)}
          loading={statusLoading}
        >
          {t('donations.complete-run')}
        </LoadingButton>
      );
    } else if (!canUseActionButtons && status !== 'COMPLETED') {
      // COMPLETED falls through: donors/recipients may edit completed weights.
      return null;
    } else {
      switch (status) {
        case 'ASSIGNED':
          return (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <LoadingButton
                  color="primary"
                  variant="contained"
                  fullWidth
                  sx={{
                    borderRadius: '50px',
                  }}
                  onClick={handleBeginPickup}
                  loading={statusLoading}
                >
                  {t('donations.begin-pickup')}
                </LoadingButton>
              </Grid>
              {!user.is_driver && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <LoadingButton
                    color="primary"
                    variant="outlined"
                    fullWidth
                    sx={{
                      borderRadius: '50px',
                    }}
                    onClick={() => {
                      setDriverAction('re-assign');
                      setShowDriversSelector(true);
                    }}
                    loading={statusLoading}
                  >
                    {t('donations.re-assign-run')}
                  </LoadingButton>
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: 6 }}>
                <LoadingButton
                  color="primary"
                  variant="outlined"
                  fullWidth
                  sx={{
                    borderRadius: '50px',
                    whiteSpace: 'nowrap',
                    minWidth: '100%',
                  }}
                  onClick={handleUnAssignRunner}
                  loading={unassignLoading}
                >
                  {unassignRunLabel}
                </LoadingButton>
              </Grid>
              {canAddWeights && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <LoadingButton
                    color="primary"
                    variant="outlined"
                    fullWidth
                    sx={{
                      borderRadius: '50px',
                    }}
                    onClick={() => setShowCompleteModal(true)}
                    loading={statusLoading}
                  >
                    {t('donations.add-weights')}
                  </LoadingButton>
                </Grid>
              )}
            </Grid>
          );
        case 'PICKUP_IN_PROGRESS':
          return (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <LoadingButton
                  color="primary"
                  variant="contained"
                  fullWidth
                  sx={{
                    borderRadius: '50px',
                  }}
                  onClick={handleBeginDelivery}
                  loading={statusLoading}
                >
                  {t('donations.begin-delivery')}
                </LoadingButton>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <LoadingButton
                  color="primary"
                  variant="outlined"
                  fullWidth
                  sx={{
                    borderRadius: '50px',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={handleUnAssignRunner}
                  loading={unassignLoading}
                >
                  {unassignRunLabel}
                </LoadingButton>
              </Grid>
              {canAddWeights && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <LoadingButton
                    color="primary"
                    variant="outlined"
                    fullWidth
                    sx={{
                      borderRadius: '50px',
                    }}
                    onClick={() => setShowCompleteModal(true)}
                    loading={statusLoading}
                  >
                    {t('donations.add-weights')}
                  </LoadingButton>
                </Grid>
              )}
            </Grid>
          );
        case 'DELIVERY_IN_PROGRESS':
          return (
            <LoadingButton
              color="primary"
              variant="contained"
              fullWidth
              sx={{
                borderRadius: '50px',
              }}
              onClick={() => setShowCompleteModal(true)}
              loading={statusLoading}
            >
              {t('donations.complete-run')}
            </LoadingButton>
          );
        case 'COMPLETED':
          if (data.is_weights_entered && user.is_driver) {
            if (drivers.some((d) => d.user_id === user.id)) {
              return <UpdateCompletionData />;
            } else {
              return null;
            }
          } else {
            return <UpdateCompletionData />;
          }
        case 'OPEN':
        case null:
          return (
            <LoadingButton
              color="primary"
              variant="contained"
              fullWidth
              sx={{
                borderRadius: '50px',
              }}
              onClick={() => {
                if (user.is_driver) {
                  handleClaim();
                } else {
                  setDriverAction('claim');
                  setShowDriversSelector(true);
                }
              }}
              loading={statusLoading}
            >
              {t('donations.claim-this-run')}
            </LoadingButton>
          );
        default:
          return null;
      }
    }
  };

  const isReadOnly = useMemo(
    () => user.is_corporate || user.account_type === 'ADMIN',
    [user]
  );

  const handleEditRun = () =>
    history.push(PATH_DASHBOARD.editRecurringFoodRun(data.parent_id));

  const handleEditFoodRunSingleInstance = () =>
    history.push(PATH_DASHBOARD.editFoodRunSingleInstance(data.id));

  const handleNotify = async ({ distance, driverIds }) => {
    try {
      if (Array.isArray(driverIds) && driverIds.length > 0) {
        await foodRun.requestDriversSelected({
          distance,
          id: data.id,
          driverIds,
        });
      } else {
        await foodRun.requestDriversSMS({ distance, id: data.id });
      }
      enqueueSnackbar(t('donations.request-sent-out-over-sms'), {
        variant: 'success',
      });
    } catch (err) {
      console.error(err);
      enqueueSnackbar(err.message, { variant: 'error' });
    }
  };

  const handleChangeRecipientSuccess = () => {
    setShowChangeRecipientModal(false);
    enqueueSnackbar('Recipient changed successfully', {
      variant: 'success',
    });
    getFoodRunData();
  };

  const isRecipientSameAsFoodMover =
    data.receiver_team === data.donation_team_id;
  const sourceLocation = foodMoverLocation;
  const destinationLocation = isRecipientSameAsFoodMover
    ? donorLocation
    : receiverLocation;
  const waypoints = isRecipientSameAsFoodMover ? [] : [donorLocation];
  const isFoodMoverTeam = data.food_mover_team_id === user.team_id;
  const canEditRun = canEditFoodRun({ user, foodRun: data });

  // Check if user can change recipient (food mover team, within 30 days and completed)
  const daysSinceScheduled = moment().diff(moment(data.end_date), 'days');
  const canChangeRecipient =
    isFoodMoverTeam && daysSinceScheduled <= 30 && isCompleted;

  const timeZoneId = useMemo(
    () => data.donor_timezone_id ?? user.timezone_id,
    [data, user]
  );

  useEffect(() => {
    setIsFoodRunEditable(
      // Incomplete/past runs can still be corrected while they are open or
      // reserved. COMPLETED remains editable so completion details can be
      // corrected from the run details page.
      Boolean(data?.id) &&
        (!data?.food_run_status ||
          [
            'OPEN',
            'WAITING_FOR_APPROVAL',
            'ASSIGNED',
            'AWAITING_DRIVER',
            'COMPLETED',
          ].includes(data.food_run_status))
    );
  }, [data]);
  return (
    <Grid spacing={2} container>
      {showCreateDonationDialog && (
        <DialogCreateDonation
          open={showCreateDonationDialog}
          onClose={() => {
            setShowCreateDonationDialog(false);
            setPatternData(null);
          }}
          isCopy
          isFoodRun
          initialDonation={data}
          patternData={patternData}
        />
      )}

      {showDriversSelector && driverAction && (
        <DriverSelectorModal
          open={showDriversSelector}
          donation={data}
          isFoodRun
          onClose={() => setShowDriversSelector(false)}
          handleDriverAssign={({ drivers }) => {
            if (driverAction == 'claim') {
              handleClaim({ drivers });
            } else {
              handleReAssignRescue({ drivers });
            }
            setShowDriversSelector(false);
            setDriverAction(null);
          }}
        />
      )}
      <Grid size={{ xs: 12 }}>
        <Stack
          gap={2}
          direction={{
            xs: 'column-reverse',
            sm: 'row',
          }}
          alignItems={{
            xs: 'stretch',
            sm: 'center',
          }}
          justifyContent="end"
        >
          {loadingFoodRun ? (
            <>
              <Skeleton
                sx={{
                  display: {
                    xs: 'none',
                    sm: 'block',
                  },
                }}
                variant="rounded"
                width={240}
                height={36}
              />
              <Skeleton
                sx={{
                  display: {
                    xs: 'block',
                    sm: 'none',
                  },
                }}
                variant="rounded"
                width={'100%'}
                height={36}
              />
            </>
          ) : !isReadOnly ? (
            <Box textAlign="center">
              <ActionButton status={data.food_run_status} />
            </Box>
          ) : null}
          <Stack direction="row" justifyContent="end">
            {loadingFoodRun ? (
              <Skeleton variant="circular" height={40} width={40} />
            ) : (
              !isCompleted &&
              !user.is_driver &&
              !isReadOnly && (
                <MoreButton
                  sx={{
                    color: (t) => t.palette.primary.main,
                  }}
                  Icon={MoreVert}
                  displayButtonForSingleOption={false}
                  options={[
                    {
                      icon: <CreateRounded />,
                      showIf: canEditRun,
                      text: t('donations.edit-food-run'),
                      action: () => setShowEditConfirmModal(true),
                    },
                    {
                      icon: <CopyAll />,
                      showIf: canEditRun,
                      text: t('donations.copy-run'),
                      action: () => {
                        if (data.parent_id) {
                          donations
                            .getRecurringPatternById({
                              id: data.parent_id,
                            })
                            .then((patternDataResponse) => {
                              setPatternData(patternDataResponse);
                              setShowCreateDonationDialog(true);
                            });
                        } else {
                          setShowCreateDonationDialog(true);
                        }
                      },
                    },
                    {
                      icon: <MarkunreadRounded />,
                      showIf: !drivers.length && canRequestForDrivers,
                      text: t('donations.request-for-drivers-sms'),
                      action: () => setShowDriverReqModal(true),
                    },
                    {
                      icon: <CancelRounded />,
                      text: t('donations.cancel-run'),
                      action: () => setShowCancelConfirmModal(true),
                    },
                  ]}
                ></MoreButton>
              )
            )}
            {user.account_type === 'ADMIN' ? (
              <MoreButton
                sx={{
                  color: (t) => t.palette.primary.main,
                }}
                Icon={MoreVert}
                displayButtonForSingleOption={false}
                options={[
                  {
                    icon: <Delete />,
                    text: t('donations.delete'),
                    action: () => setShowDeleteRunModal(true),
                  },
                ]}
              ></MoreButton>
            ) : null}
          </Stack>
        </Stack>
      </Grid>
      <Grid size={{ xs: 12, md: 8 }}>
        <Typography style={{ fontWeight: 'bold' }} variant="h3">
          {loadingFoodRun ? <Skeleton /> : data?.title}
        </Typography>

        <Typography color="gray" variant="body2">
          {t('donations.FOOD-RUN')}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        {loadingFoodRun ? (
          <Skeleton variant="rounded" width="100%" height={75} />
        ) : (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography
                sx={{
                  whiteSpace: 'nowrap',
                }}
                variant="h6"
              >
                {t('donations.food-run-status')}
              </Typography>
              {
                <Chip
                  sx={{
                    mt: 0.5,
                    textTransform: 'capitalize',
                    width: '100%',
                    borderRadius: 1,
                  }}
                  label={getStatus(data.food_run_status)}
                  color="primary"
                  variant="ghost"
                />
              }
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="h6">{t('donations.frequency')}</Typography>
              {
                <Chip
                  sx={{
                    mt: 0.5,
                    textTransform: 'capitalize',
                    width: '100%',
                    borderRadius: 1,
                  }}
                  label={
                    data?.is_recurring
                      ? getType(data?.recurring_type)
                      : t('donations.single-run')
                  }
                  color="primary"
                  variant="ghost"
                />
              }
            </Grid>
          </Grid>
        )}
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Typography sx={{ flex: 1 }} variant="body1">
          {data?.description}
        </Typography>
      </Grid>

      <Grid size={{ xs: 12, md: 8 }}>
        {loadingFoodRun ? (
          <Skeleton variant="rounded" width="100%" height={450} />
        ) : (
          <Card sx={{ p: 3 }}>
            <Box>
              <Grid container spacing={2.5}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box>
                    <TitleInfo variant="h5">
                      {t('donations.pickup-info')}
                    </TitleInfo>
                    <Grid container spacing={1}>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="overline" color={labelColor}>
                          {t('donations.donor').toUpperCase()}:
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography fontWeight={900} variant="body2">
                          {user.team_id === data.donor_team_id ? (
                            data.donor_business_name
                          ) : (
                            <Link
                              component={RouterLink}
                              to={viewOrg(data.donor_team)}
                              underline="hover"
                            >
                              {data.donor_business_name}
                            </Link>
                          )}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="overline" color={labelColor}>
                          {t('donations.address').toUpperCase()}:
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography fontWeight={900} variant="body2">
                          {donorLocation.formatted_address}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="overline" color={labelColor}>
                          {t('donations.pickup-date-time')}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography fontWeight={900} variant="body2">
                          {
                            <>
                              {formatDisplayDate(
                                moment.utc(data.start_date).tz(timeZoneId),
                                'weekdayDateLong',
                                { time: true }
                              )}{' '}
                              -
                              {formatDisplayDate(
                                moment.utc(data.end_date).tz(timeZoneId),
                                'time'
                              )}
                            </>
                          }
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="overline" color={labelColor}>
                          {t('donations.phone')}:
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography fontWeight={900} variant="body2">
                          {donorContact.phone}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="overline" color={labelColor}>
                          {t('donations.vehicle-type')}:
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography fontWeight={900} variant="body2">
                          {
                            vehicleLabelsWithUnit.find(
                              (item) => item.vehicle === data.vehicle
                            )?.label
                          }
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="overline" color={labelColor}>
                          {t('donations.vehicle')}:
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography fontWeight={900} variant="body2">
                          {displayedVehicleName || t('donations.none-assigned')}
                        </Typography>
                      </Grid>
                      {data.donor_rescue_rating && (
                        <>
                          <Grid size={{ xs: 6 }}>
                            <Typography variant="overline" color={labelColor}>
                              {t('donations.donor-rating')}:
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 6 }}>
                            <Rating
                              name="donor-rescue-rating"
                              defaultValue={data.donor_rescue_rating || 3}
                              readOnly
                            />
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Box>
                  {data.donor_rescue_rating_notes && isFoodMoverTeam && (
                    <Box mt={2}>
                      <TitleInfo variant="h5">
                        {t('donations.donor-feedback')}
                      </TitleInfo>
                      <InfoContainer>
                        <Typography
                          sx={{
                            whiteSpace: 'pre-wrap',
                            overflowWrap: 'break-word',
                          }}
                          variant="body2"
                        >
                          {data.donor_rescue_rating_notes}
                        </Typography>
                      </InfoContainer>
                    </Box>
                  )}
                  {data.pickup_instructions && (
                    <Box mt={2}>
                      <TitleInfo variant="h5">
                        {t('donations.pickup-instructions')}
                      </TitleInfo>
                      <InfoContainer>
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            overflowWrap: 'break-word',
                          }}
                        >
                          {data.pickup_instructions}
                        </Typography>
                      </InfoContainer>
                    </Box>
                  )}
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box>
                    <TitleInfo variant="h5">
                      {t('donations.dropoff-info')}
                    </TitleInfo>
                    <Grid container spacing={1}>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="overline" color={labelColor}>
                          {t('donations.recipient')}:
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography fontWeight={900} variant="body2">
                            {user.team_id === data.recipient_team_id ? (
                              data.receiver_business_name
                            ) : (
                              <Link
                                component={RouterLink}
                                to={viewOrg(data.receiver_team)}
                                underline="hover"
                              >
                                {data.receiver_business_name}
                              </Link>
                            )}
                          </Typography>
                          {canChangeRecipient && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              onClick={() => setShowChangeRecipientModal(true)}
                              sx={{
                                ml: 1,
                                fontSize: '0.75rem',
                                minWidth: 'auto',
                              }}
                            >
                              {t('common.change')}
                            </Button>
                          )}
                        </Stack>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="overline" color={labelColor}>
                          {t('donations.address').toUpperCase()}:
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography fontWeight={900} variant="body2">
                          {receiverLocation.formatted_address}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="overline" color={labelColor}>
                          {t('donations.phone').toUpperCase()}:
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography fontWeight={900} variant="body2">
                          {receiverContact.phone}
                        </Typography>
                      </Grid>
                      {data.recipient_rescue_rating && (
                        <>
                          <Grid size={{ xs: 6 }}>
                            <Typography variant="overline" color={labelColor}>
                              {t('donations.recipient-rating')}:
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 6 }}>
                            <Rating
                              name="recipient-rescue-rating"
                              defaultValue={data.recipient_rescue_rating}
                              readOnly
                            />
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Box>
                  {data.recipient_rescue_rating_notes && isFoodMoverTeam && (
                    <Box mt={2}>
                      <TitleInfo variant="h5">
                        {t('donations.recipient-feedback')}
                      </TitleInfo>
                      <InfoContainer>
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            overflowWrap: 'break-word',
                          }}
                        >
                          {data.recipient_rescue_rating_notes}
                        </Typography>
                      </InfoContainer>
                    </Box>
                  )}
                  {data.dropoff_instructions && (
                    <Box mt={2}>
                      <TitleInfo variant="h5">
                        {t('donations.dropoff-instructions')}
                      </TitleInfo>
                      <InfoContainer>
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            overflowWrap: 'break-word',
                          }}
                        >
                          {data.dropoff_instructions}
                        </Typography>
                      </InfoContainer>
                    </Box>
                  )}
                </Grid>
              </Grid>
              <Grid mt={1} container spacing={2.5}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TitleInfo variant="h5">
                    {t('donations.runner-details')}
                  </TitleInfo>
                  <Grid mt={0.3} container spacing={1}>
                    {drivers.length > 0 ? (
                      drivers.map((user) => (
                        <Grid key={user.id} mt={0.3} container spacing={1}>
                          <Grid size={{ xs: 7 }}>
                            <Typography fontWeight={900} variant="body2">
                              {user.user_name}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 5 }}>
                            <Typography fontWeight={900} variant="body2">
                              {user.user_email}
                            </Typography>
                          </Grid>
                        </Grid>
                      ))
                    ) : (
                      <Grid size={{ xs: 12 }}>
                        <Typography fontWeight={900} variant="body2">
                          {t('donations.no-drivers-assigned')}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                {data?.notes && (
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TitleInfo variant="h5">
                      {t('donations.completion-notes')}
                    </TitleInfo>
                    <InfoContainer>
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          overflowWrap: 'break-word',
                        }}
                      >
                        {data.notes}
                      </Typography>
                    </InfoContainer>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Card>
        )}
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        {loadingFoodRun ? (
          <>
            <Skeleton variant="rounded" width="100%" height={300} />
            <Skeleton
              variant="rounded"
              sx={{
                mt: 2,
              }}
              width="100%"
              height={36}
            />
          </>
        ) : (
          <Card sx={{ p: 3 }}>
            <Box
              sx={{ cursor: 'pointer' }}
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              textAlign="center"
            >
              <>
                <Box
                  width={'100%'}
                  height={300}
                  margin="auto"
                  marginBottom={2}
                  alignSelf="center"
                >
                  <GoogleDirectionsMap
                    sourceLocation={sourceLocation}
                    destinationLocation={destinationLocation}
                    waypoints={waypoints}
                  />
                </Box>
                <Button
                  fullWidth
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    const URL = `https://www.google.com/maps/dir/?api=1&origin=${sourceLocation.formatted_address}&origin_place_id=${sourceLocation.place_id}&destination=${destinationLocation.formatted_address}&destination_place_id=${destinationLocation.place_id}`;
                    const wayPointComp = isRecipientSameAsFoodMover
                      ? ''
                      : `&waypoints=${_get(
                          waypoints,
                          '[0].formatted_address'
                        )}&waypoint_place_ids=${_get(
                          waypoints,
                          '[0].place_id'
                        )}&travelmode=driving&dir_action=navigate`;
                    window.open(URL + wayPointComp, '_blank');
                  }}
                >
                  {t('donations.open-map-in-new-tab')}
                </Button>
              </>
            </Box>
          </Card>
        )}
        <ConfirmModal
          title={t('donations.are-you-sure-you-want-to-permanently-delete')}
          confirmMessage={t('donations.this-action-cannot-be-undone')}
          cancelLabel={t('donations.close')}
          okLabel={t('donations.delete-run')}
          open={showDeleteRunModal}
          handleCancel={() => setShowDeleteRunModal(false)}
          onClick={hardDeleteDonationByAdmin}
        />
        <CompleteFoodRunModal
          open={showCompleteModal}
          onClose={() => setShowCompleteModal(false)}
          donorBusinessName={data.donor_business_name}
          receiverBusinessName={data.receiver_business_name}
          handleComplete={handleComplete}
          rescueId={data.rescue_id}
          noDonation={data.no_donation}
          handleUpdateCompletionData={handleUpdateCompletionData}
          foodWeights={parseFoodWeights(data.estimated_weights)}
          images={JSON.parse(data['completion_images'] ?? '[]')}
          isCompleted={data.food_run_status === 'COMPLETED'}
          weightsOnly={shouldUseWeightsOnlyMode({
            user,
            foodRun: data,
            drivers,
            status: data.food_run_status,
          })}
          hasSavedWeights={Boolean(data.is_weights_entered)}
          rescuedFoodWeights={rescuedFoodWeights}
          isRecipientSameAsFoodMover={isRecipientSameAsFoodMover}
          isLoading={updateCompletionDataLoading || statusLoading}
          startLoading={() => setUpdateCompletionDataLoading(true)}
          stopLoading={() => setUpdateCompletionDataLoading(false)}
          isFoodRun={true}
          notes={data.notes}
        />
        <EditConfirmModal
          open={showEditConfirmModal}
          handleClose={() => setShowEditConfirmModal(false)}
          handleEditFoodRun={handleEditRun}
          handleEditFoodRunSingleInstance={handleEditFoodRunSingleInstance}
          isRecurring={!data.is_pattern_cancelled && data.is_recurring}
        />
        {showDriverReqModal && (
          <RequestDriversModal
            donationId={data.id}
            open={showDriverReqModal}
            onClose={() => setShowDriverReqModal(false)}
            onNotify={handleNotify}
          />
        )}
        <CancelConfirmModal
          open={showCancelConfirmModal}
          handleClose={() => setShowCancelConfirmModal(false)}
          handleCancelFoodRun={() => {
            setShowCancelConfirmModal(false);
            setShowCancelRecurringInstancesConfirmModal(true);
          }}
          handleCancelFoodRunSingleInstance={() => {
            setShowCancelConfirmModal(false);
            setShowCancelSingleInstanceConfirmModal(true);
          }}
          isRecurring={data.is_recurring}
          cancelLoadingInstance={cancelLoadingInstance}
          cancelLoadingBulk={cancelLoadingBulk}
        />
        <DonationCancelConfirmationModal
          open={showCancelSingleInstanceConfirmModal}
          handleCancel={() => setShowCancelSingleInstanceConfirmModal(false)}
          donation={data}
          isFoodRun
          type="onetime"
        />
        <DonationCancelConfirmationModal
          open={showCancelRecurringInstancesConfirmModal}
          handleCancel={() =>
            setShowCancelRecurringInstancesConfirmModal(false)
          }
          donation={data}
          isFoodRun
          type="recurring"
        />
        <ChangeRecipientModal
          open={showChangeRecipientModal}
          onClose={() => setShowChangeRecipientModal(false)}
          donationId={data.id}
          currentRecipientTeamId={data.receiver_team}
          currentRecipientName={data.receiver_business_name}
          endDate={data.end_date}
          onSuccess={handleChangeRecipientSuccess}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <DonationCargoDetails
          isLoading={loadingFoodRun}
          donation={data}
          user={user}
          setShowCompleteModal={setShowCompleteModal}
          isDonationEditable={isFoodRunEditable}
          isDonorTeam={isFoodMoverTeam}
          setShowEditConfirmModal={setShowEditConfirmModal}
          handleEditDonationSingleInstance={handleEditFoodRunSingleInstance}
          totalFoodWeight={totalFoodWeight}
          parsedFoodWeightObject={
            data?.food_run_status === 'COMPLETED' || data?.is_weights_entered
              ? rescuedFoodWeightsArray
              : parsedFoodWeightObject
          }
        />
      </Grid>

      {loadingFoodRun ? (
        <Grid size={{ xs: 12 }}>
          <Skeleton variant="rounded" height={274} width="100%" />
        </Grid>
      ) : (
        <Grid size={{ xs: 12 }}>
          <Card sx={{ p: 3 }}>
            <Stack gap={1}>
              <TitleInfo variant="h5">{t('donations.images')}</TitleInfo>
              {images?.length > 0 ? (
                <DonationImageGrid
                  images={images?.map(
                    (key) => `${S3_BASE_URL}/${BUCKET_NAME}/${key}`
                  )}
                />
              ) : (
                <CardEmptyState
                  text={t('donations.there-are-no-images-for-this-food-run')}
                  sx={{ height: '150px', width: '100%' }}
                />
              )}
            </Stack>
            {(() => {
              let additionalImages = [];
              if (data.additional_images) {
                const parsed = JSON.parse(data.additional_images);
                if (Array.isArray(parsed)) {
                  additionalImages = parsed;
                }
              }
              return additionalImages.length > 0 ? (
                <Stack mt={2} gap={1}>
                  <TitleInfo variant="h5">
                    {t('donations.additional-information')}
                  </TitleInfo>
                  <DonationImageGrid
                    images={additionalImages.map(
                      (key) => `${S3_BASE_URL}/${BUCKET_NAME}/${key}`
                    )}
                  />
                </Stack>
              ) : null;
            })()}
            {isCompleted && data.completion_images && (
              <Stack mt={2} gap={1}>
                <TitleInfo variant="h5">
                  {t('donations.rescue-images')}
                </TitleInfo>
                <DonationImageGrid
                  mt={2}
                  images={JSON.parse(data.completion_images).map(
                    (url) => `${S3_BASE_URL}/${BUCKET_NAME}/${url}`
                  )}
                />
              </Stack>
            )}
          </Card>
        </Grid>
      )}

      <Grid size={{ xs: 12 }}>
        <DonationNotes donation={data} donationId={data.id} user={user} />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Card sx={{ p: 3 }}>
          <TitleInfo variant="h5">{t('donations.activity-log')}</TitleInfo>
          <DonationEventLogs
            donationId={data.id}
            tz={timeZoneId}
            status={data.food_run_status}
            recipientTeamId={data.receiver_team}
          />
        </Card>
      </Grid>
    </Grid>
  );
};

export default FoodRunInfo;

const EditConfirmModal = ({
  open,
  handleClose,
  handleEditFoodRunSingleInstance,
  handleEditFoodRun,
  isRecurring = true,
}) => {
  const { t } = useTranslation('translation');
  const { user } = useAuth();
  return (
    <Dialog maxWidth="md" fullWidth open={open} onClose={handleClose}>
      <>
        <Box
          marginBottom="15px"
          width="100%"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <DialogTitle>{t('donations.edit-run')}</DialogTitle>
        </Box>
        <DialogContent>
          <Typography>
            {t('donations.are-you-sure-you-want-to-edit-this-run')}
          </Typography>
        </DialogContent>
        {isRecurring ? (
          <DialogActions>
            <Button onClick={handleClose}>{t('donations.close')}</Button>
            <Button
              variant="contained"
              onClick={handleEditFoodRunSingleInstance}
            >
              {t('donations.edit-this-run')}
            </Button>
            {!user.is_driver ? (
              <Button variant="contained" onClick={handleEditFoodRun}>
                {t('donations.edit-all-future-runs')}
              </Button>
            ) : null}
          </DialogActions>
        ) : (
          <DialogActions>
            <Button onClick={handleClose}>{t('donations.close')}</Button>
            <Button
              variant="contained"
              onClick={handleEditFoodRunSingleInstance}
            >
              {t('donations.edit-run')}
            </Button>
          </DialogActions>
        )}
      </>
    </Dialog>
  );
};

const RequestDriversModal = ({ open, onClose, onNotify, donationId }) => {
  const { t } = useTranslation('translation');
  const { user } = useAuth();
  const history = useHistory();
  const unit = useMemo(() => getDefaultUnit(DISTANCE_UNITS, user), [user]);
  const [distanceValue, setDistanceValue] = useState(15);
  const [debouncedDistanceValue, setDebouncedDistanceValue] = useState(15);
  useEffect(() => {
    const h = setTimeout(() => setDebouncedDistanceValue(distanceValue), 600);
    return () => clearTimeout(h);
  }, [distanceValue]);
  const distanceInMi = useMemo(
    () => new Distance(debouncedDistanceValue, unit.value).into('mi').value,
    [debouncedDistanceValue, unit]
  );
  const { drivers, isLoading, error, clearError } = useNotifyableDrivers(
    donationId,
    distanceInMi
  );
  const [selectedDriverIds, setSelectedDriverIds] = useState([]);
  const [hideNotified, setHideNotified] = useState(false);

  const [smsStatus, setSmsStatus] = useState(null);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    smsBilling
      .getStatus()
      .then((s) => {
        if (!cancelled) setSmsStatus(s);
      })
      .catch(() => {
        if (!cancelled)
          setSmsStatus({ canBroadcast: false, hasBilling: false });
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Default to disabled until the billing status resolves, so the modal can't
  // be submitted during the loading window (the server would reject with 403).
  const broadcastDisabled = smsStatus ? !smsStatus.canBroadcast : true;
  const visibleDrivers = useMemo(
    () =>
      (drivers || []).filter(
        (d) => !hideNotified || (d.notifiedCount ?? 0) === 0
      ),
    [drivers, hideNotified]
  );
  const applicableCount = visibleDrivers.length;
  useEffect(() => {
    setSelectedDriverIds((_prev) => {
      const all = visibleDrivers.map((d) => d.driverId);
      // default to all when list changes
      return all;
    });
  }, [visibleDrivers]);

  const ReqDriverSchema = Yup.object().shape({
    distance: Yup.number()
      .min(0, t('donations.distance-greater-than-zero'))
      .max(50, t('donations.distance-less-than-50'))
      .required(t('donations.distance-required')),
  });

  const handleSubmit = useCallback(
    async ({ distance }) => {
      const distanceInMiLocal = new Distance(distance, unit.value).into(
        'mi'
      ).value;
      await onNotify({
        distance: distanceInMiLocal,
        driverIds: selectedDriverIds,
      });
      onClose();
    },
    [onNotify, onClose, unit, selectedDriverIds]
  );

  const handleToggle = (driverId) => {
    setSelectedDriverIds((prev) =>
      prev.includes(driverId)
        ? prev.filter((id) => id !== driverId)
        : [...prev, driverId]
    );
  };

  const handleSelectAll = () =>
    setSelectedDriverIds((drivers || []).map((d) => d.driverId));
  const handleUnselectAll = () => setSelectedDriverIds([]);

  return (
    <Dialog maxWidth="md" fullWidth open={open} onClose={onClose}>
      <Box
        marginBottom="15px"
        width="100%"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <DialogTitle>{t('donations.request-drivers-over-sms')}</DialogTitle>
      </Box>

      <form.Form
        schema={ReqDriverSchema}
        onSubmit={handleSubmit}
        onCancel={onClose}
        readonly={broadcastDisabled}
      >
        {(values) => (
          <>
            <DialogContent>
              {smsStatus && !broadcastDisabled && smsStatus.hasBilling && (
                <Box mb={2}>
                  <Typography variant="body2">
                    {t('donations.sms-meter-credits-used', {
                      used: smsStatus.creditsUsed,
                      included: smsStatus.creditsIncluded,
                    })}
                  </Typography>
                  <LinearProgress
                    sx={{ mt: 0.5 }}
                    variant="determinate"
                    value={Math.min(
                      (Math.min(
                        smsStatus.creditsUsed,
                        smsStatus.creditsIncluded
                      ) /
                        smsStatus.creditsIncluded) *
                        100,
                      100
                    )}
                  />
                </Box>
              )}

              {smsStatus && broadcastDisabled && (
                <Alert
                  severity="warning"
                  sx={{ mb: 2 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => {
                        onClose();
                        history.push(
                          PATH_DASHBOARD.settings.business('billing')
                        );
                      }}
                    >
                      {t('donations.sms-meter-no-plan-cta')}
                    </Button>
                  }
                >
                  {smsStatus && smsStatus.hasBilling
                    ? t('donations.sms-meter-limit-reached')
                    : t('donations.sms-meter-no-plan-banner')}
                </Alert>
              )}

              <Typography>
                {t('donations.send-request-for-near-drivers')}
              </Typography>

              <form.Slider
                id="distance"
                label={`${t('donations.distance', { unit: unit.label })}`}
                defaultValue={distanceValue}
                fullWidth
                required
                max={50}
                onChange={(_, v) => {
                  const val = Array.isArray(v) ? v[0] : v;
                  setDistanceValue(val);
                }}
              />

              <Typography>
                {t('donations.will-send-a-sms-message-to')}&nbsp;
                {applicableCount}
                &nbsp; {t('donations.drivers-count')}.
              </Typography>

              {error ? (
                <Alert severity="error" sx={{ mt: 1 }} onClose={clearError}>
                  {error?.message}
                </Alert>
              ) : null}

              <Box mt={2}>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleSelectAll}
                    disabled={broadcastDisabled}
                  >
                    {t('common.select-all')}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleUnselectAll}
                    disabled={broadcastDisabled}
                  >
                    {t('common.unselect-all')}
                  </Button>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={hideNotified}
                        onChange={(e) => setHideNotified(e.target.checked)}
                        disabled={broadcastDisabled}
                      />
                    }
                    label={t('donations.hide-previously-notified')}
                  />
                </Stack>
                <Divider />
                <Box mt={1}>
                  {isLoading ? (
                    <>
                      <Skeleton variant="rounded" height={32} />
                      <Skeleton variant="rounded" height={32} sx={{ mt: 1 }} />
                      <Skeleton variant="rounded" height={32} sx={{ mt: 1 }} />
                    </>
                  ) : visibleDrivers.length === 0 && !error ? (
                    <Typography color="text.secondary">
                      {t('donations.no-drivers-within-distance', {
                        distance: values.distance ?? distanceValue,
                        unit: unit.label,
                      })}
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {visibleDrivers.map((d) => (
                        <Box
                          key={d.driverId}
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedDriverIds.includes(d.driverId)}
                                onChange={() => handleToggle(d.driverId)}
                                disabled={broadcastDisabled}
                              />
                            }
                            label={d.name}
                          />
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Chip
                              size="small"
                              label={`${Math.round(d.distance)} ${t('common.mi')}`}
                            />
                            <Chip
                              size="small"
                              color="info"
                              label={t('donations.notified-count-badge', {
                                count: d.notifiedCount,
                              })}
                            />
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Box>
              </Box>
            </DialogContent>

            <DialogActions>
              <form.Button action="cancel">{t('donations.cancel')}</form.Button>
              <Box flex={1} />
              {error == null &&
              (drivers || []).length > 0 &&
              selectedDriverIds.length === 0 ? (
                <Typography color="text.secondary" sx={{ mr: 2 }}>
                  {t('donations.select-at-least-one-driver')}
                </Typography>
              ) : null}
              <form.Button
                action="submit"
                variant="contained"
                disabled={
                  Boolean(error) ||
                  (drivers || []).length === 0 ||
                  selectedDriverIds.length === 0 ||
                  broadcastDisabled
                }
              >
                {t('donations.send')}
              </form.Button>
            </DialogActions>
          </>
        )}
      </form.Form>
    </Dialog>
  );
};

const CancelConfirmModal = ({
  open,
  handleClose,
  handleCancelFoodRunSingleInstance,
  handleCancelFoodRun,
  isRecurring = true,
  cancelLoadingInstance,
  cancelLoadingBulk,
}) => {
  const { t } = useTranslation('translation');
  return (
    <Dialog maxWidth="md" fullWidth open={open} onClose={handleClose}>
      <>
        <Box
          marginBottom="15px"
          width="100%"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <DialogTitle>{t('donations.cancel-run')}</DialogTitle>
        </Box>
        <DialogContent>
          <Typography>
            {t('donations.are-you-sure-you-want-to-cancel-this-run')}
          </Typography>
        </DialogContent>
        {isRecurring ? (
          <DialogActions>
            <Button onClick={handleClose}>{t('donations.cancel')}</Button>
            <LoadingButton
              variant="contained"
              onClick={handleCancelFoodRunSingleInstance}
              loading={cancelLoadingInstance}
            >
              {t('donations.cancel-this-run')}
            </LoadingButton>
            <LoadingButton
              variant="contained"
              onClick={handleCancelFoodRun}
              loading={cancelLoadingBulk}
            >
              {t('donations.cancel-all-future-runs')}
            </LoadingButton>
          </DialogActions>
        ) : (
          <DialogActions>
            <Button onClick={handleClose}>{t('donations.close')}</Button>
            <Button
              variant="contained"
              onClick={handleCancelFoodRunSingleInstance}
            >
              {t('donations.cancel-run')}
            </Button>
          </DialogActions>
        )}
      </>
    </Dialog>
  );
};
